import { LinkType, RichTextNodeType } from '@prismicio/types';
import { composeSerializers, wrapMapSerializer, serialize, Element } from '@prismicio/richtext';
export { Element, asText } from '@prismicio/richtext';
import escapeHtml from 'escape-html';

const asDate = (dateOrTimestampField) => {
  if (!dateOrTimestampField) {
    return null;
  }
  if (dateOrTimestampField.length === 24) {
    return new Date(dateOrTimestampField.replace(/(\+|-)(\d{2})(\d{2})$/, ".000$1$2:$3"));
  } else {
    return new Date(dateOrTimestampField);
  }
};

const asLink = (linkField, linkResolver) => {
  if (!linkField) {
    return null;
  }
  switch (linkField.link_type) {
    case LinkType.Media:
    case LinkType.Web:
      return "url" in linkField ? linkField.url : null;
    case LinkType.Document:
      if ("url" in linkField && linkField.url) {
        return linkField.url;
      } else if ("id" in linkField) {
        return linkResolver ? linkResolver(linkField) : null;
      } else {
        return null;
      }
    case LinkType.Any:
    default:
      return null;
  }
};

const getLabel = (node) => {
  return "data" in node && "label" in node.data ? ` class="${node.data.label}"` : "";
};
const serializeStandardTag = (tag, node, children) => {
  return `<${tag}${getLabel(node)}>${children.join("")}</${tag}>`;
};
const serializePreFormatted = (node) => {
  return `<pre${getLabel(node)}>${escapeHtml(node.text)}</pre>`;
};
const serializeImage = (linkResolver, node) => {
  let imageTag = `<img src="${node.url}" alt="${escapeHtml(node.alt)}"${node.copyright ? ` copyright="${escapeHtml(node.copyright)}"` : ""} />`;
  if (node.linkTo) {
    imageTag = serializeHyperlink(linkResolver, {
      type: RichTextNodeType.hyperlink,
      data: node.linkTo,
      start: 0,
      end: 0
    }, [imageTag]);
  }
  return `<p class="block-img">${imageTag}</p>`;
};
const serializeEmbed = (node) => {
  return `<div data-oembed="${node.oembed.embed_url}" data-oembed-type="${node.oembed.type}" data-oembed-provider="${node.oembed.provider_name}"${getLabel(node)}>${node.oembed.html}</div>`;
};
const serializeHyperlink = (linkResolver, node, children) => {
  switch (node.data.link_type) {
    case LinkType.Web: {
      return `<a href="${escapeHtml(node.data.url)}" target="${node.data.target}" rel="noopener noreferrer"${getLabel(node)}>${children.join("")}</a>`;
    }
    case LinkType.Document: {
      return `<a href="${asLink(node.data, linkResolver)}"${getLabel(node)}>${children.join("")}</a>`;
    }
    case LinkType.Media: {
      return `<a href="${node.data.url}"${getLabel(node)}>${children.join("")}</a>`;
    }
  }
};
const serializeSpan = (content) => {
  return content ? escapeHtml(content).replace(/\n/g, "<br />") : "";
};

function defaultHTMLSerializer(linkResolver, _type, node, content, children, _key) {
  switch (node.type) {
    case Element.heading1:
      return serializeStandardTag("h1", node, children);
    case Element.heading2:
      return serializeStandardTag("h2", node, children);
    case Element.heading3:
      return serializeStandardTag("h3", node, children);
    case Element.heading4:
      return serializeStandardTag("h4", node, children);
    case Element.heading5:
      return serializeStandardTag("h5", node, children);
    case Element.heading6:
      return serializeStandardTag("h6", node, children);
    case Element.paragraph:
      return serializeStandardTag("p", node, children);
    case Element.preformatted:
      return serializePreFormatted(node);
    case Element.strong:
      return serializeStandardTag("strong", node, children);
    case Element.em:
      return serializeStandardTag("em", node, children);
    case Element.listItem:
      return serializeStandardTag("li", node, children);
    case Element.oListItem:
      return serializeStandardTag("li", node, children);
    case Element.list:
      return serializeStandardTag("ul", node, children);
    case Element.oList:
      return serializeStandardTag("ol", node, children);
    case Element.image:
      return serializeImage(linkResolver, node);
    case Element.embed:
      return serializeEmbed(node);
    case Element.hyperlink:
      return serializeHyperlink(linkResolver, node, children);
    case Element.label:
      return serializeStandardTag("span", node, children);
    case Element.span:
    default:
      return serializeSpan(content);
  }
}
function asHTML(richTextField, linkResolver, htmlSerializer) {
  let serializer;
  if (htmlSerializer) {
    serializer = composeSerializers(typeof htmlSerializer === "object" ? wrapMapSerializer(htmlSerializer) : htmlSerializer, defaultHTMLSerializer.bind(null, linkResolver));
  } else {
    serializer = defaultHTMLSerializer.bind(null, linkResolver);
  }
  return serialize(richTextField, serializer).join("");
}

const documentToLinkField = (prismicDocument) => {
  var _a, _b;
  return {
    link_type: LinkType.Document,
    id: prismicDocument.id,
    uid: (_a = prismicDocument.uid) != null ? _a : void 0,
    type: prismicDocument.type,
    tags: prismicDocument.tags,
    lang: prismicDocument.lang,
    url: (_b = prismicDocument.url) != null ? _b : void 0,
    slug: prismicDocument.slugs[0],
    ...Object.keys(prismicDocument.data).length > 0 ? { data: prismicDocument.data } : {}
  };
};

const documentAsLink = (prismicDocument, linkResolver) => {
  return asLink(documentToLinkField(prismicDocument), linkResolver);
};

export { asDate, asHTML, asLink, documentAsLink, documentToLinkField };
